import React, { useState, useEffect } from 'react';
import {
  UserPlus,
  Mail,
  Lock,
  User,
  Eye,
  EyeOff,
  Phone,
  Building2,
  Briefcase,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { civilities } from '../data/quoteFormOptions';
import DashFormStepper from './dashboard/DashFormStepper';
import '../pages/AuthPage.css';

const REGISTER_STEPS = ['register'];
const EDIT_STEPS = ['edit'];

const STEP_LABELS = {
  identity: 'Identité',
  account: 'Compte',
  profile: 'Profil & conditions',
  register: 'Inscription',
  edit: 'Informations',
};

const rowStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '18px',
};

const radioGroupStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: '12px',
};

const radioStyle = (selected) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '10px 12px',
  border: `2px solid ${selected ? 'var(--teal)' : 'var(--gray-200)'}`,
  borderRadius: '12px',
  background: selected ? 'rgba(26, 122, 122, 0.08)' : '#fff',
  cursor: 'pointer',
  fontSize: '0.85rem',
  fontWeight: '700',
  color: selected ? 'var(--teal)' : 'var(--navy)',
});

const checkboxStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  padding: '10px 14px',
  border: '1px solid var(--gray-200)',
  borderRadius: '12px',
  background: '#fff',
  fontSize: '0.85rem',
  fontWeight: '600',
  color: 'var(--navy)',
  lineHeight: '1.4',
  cursor: 'pointer',
};

const sectionDividerStyle = {
  margin: '20px 0 14px',
  paddingTop: '16px',
  borderTop: '1px solid var(--gray-200)',
  fontSize: '0.78rem',
  fontWeight: 800,
  color: 'var(--teal)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

export default function ClientRegisterForm({
  mode = 'register',
  initialValues,
  onSubmit,
  submitLabel,
  successMessage,
  onSuccess,
  title,
  subtitle,
  footer,
}) {
  const { t } = useLanguage();
  const editMode = mode === 'edit';
  const steps = editMode ? EDIT_STEPS : REGISTER_STEPS;

  const init = initialValues || {};
  const nameParts = String(init.name || '').trim().split(/\s+/);
  const [form, setForm] = useState({
    civility: init.civility || '',
    firstName: init.firstName || nameParts[0] || '',
    lastName: init.lastName || nameParts.slice(1).join(' ') || '',
    email: init.email || '',
    phone: init.phone || '',
    password: '',
    confirmPassword: '',
    clientType: init.clientType || 'particulier',
    organizationName: init.organizationName || '',
    taxId: init.taxId || '',
    acceptTerms: false,
    acceptPrivacy: false,
    confirmInfo: false,
  });

  const [stepIdx, setStepIdx] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [attempted, setAttempted] = useState(false);

  const stepKey = steps[stepIdx];
  const isLastStep = stepIdx === steps.length - 1;
  const stepLabel = (key) => (key === 'identity' && editMode ? 'Informations personnelles' : STEP_LABELS[key]);

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const getPasswordStrength = (pwd) => {
    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[a-z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
    return score;
  };

  const getPasswordError = (pwd) => {
    if (pwd.length < 8) return 'Le mot de passe doit contenir au moins 8 caractères.';
    if (!/[A-Z]/.test(pwd)) return 'Le mot de passe doit contenir au moins une majuscule.';
    if (!/[a-z]/.test(pwd)) return 'Le mot de passe doit contenir au moins une minuscule.';
    if (!/[0-9]/.test(pwd)) return 'Le mot de passe doit contenir au moins un chiffre.';
    if (!/[^A-Za-z0-9]/.test(pwd)) return 'Le mot de passe doit contenir au moins un caractère spécial.';
    return '';
  };

  const getBlockError = (key) => {
    switch (key) {
      case 'personal': {
        const missing = !form.civility || !form.firstName || !form.lastName || !form.email
          || (editMode && !form.phone);
        if (missing) {
          return 'Veuillez remplir tous les champs obligatoires.';
        }
        if (form.firstName.trim().length < 2 || form.lastName.trim().length < 2) {
          return 'Le prénom et le nom doivent contenir au moins 2 caractères.';
        }
        if (!/^\S+@\S+\.\S+$/.test(form.email)) return 'Adresse e-mail invalide.';
        return '';
      }
      case 'account':
        {
          const pwdError = getPasswordError(form.password);
          if (pwdError) return pwdError;
        }
        if (form.password !== form.confirmPassword) return 'Les mots de passe ne correspondent pas.';
        return '';
      case 'type':
        if (!form.clientType) return 'Veuillez sélectionner un type de client.';
        if (form.clientType !== 'particulier' && !form.organizationName.trim()) {
          return 'Veuillez saisir le nom de l\'entreprise / organisation.';
        }
        return '';
      case 'terms':
        if (!form.acceptTerms || !form.acceptPrivacy) {
          return 'Vous devez accepter les conditions générales et la politique de confidentialité.';
        }
        if (!form.confirmInfo) {
          return 'Veuillez confirmer l\'exactitude des informations fournies.';
        }
        return '';
      default:
        return '';
    }
  };

  const getStepError = (key) => {
    if (key === 'identity') return getBlockError('personal');
    if (key === 'account') return editMode ? '' : getBlockError('account');
    if (key === 'register') {
      return getBlockError('personal')
        || getBlockError('account')
        || getBlockError('type')
        || getBlockError('terms');
    }
    if (key === 'profile') {
      return getBlockError('type');
    }
    if (key === 'edit') {
      return getBlockError('personal') || getBlockError('type');
    }
    return getBlockError(key);
  };

  // Re-valide en direct uniquement après un premier clic sur « Suivant » /
  // « S'inscrire » : l'erreur se met à jour quand l'utilisateur corrige.
  useEffect(() => {
    if (!attempted) return;
    setError(getStepError(stepKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempted, form, stepKey]);

  const goNext = () => {
    setAttempted(true);
    const msg = getStepError(stepKey);
    if (msg) {
      setError(msg);
      return;
    }
    setError('');
    setAttempted(false);
    if (!isLastStep) setStepIdx((s) => s + 1);
  };

  const goPrev = () => {
    setError('');
    setAttempted(false);
    if (stepIdx > 0) setStepIdx((s) => s - 1);
  };

  // Navigation via le stepper : retour libre, avancer seulement si les
  // étapes précédentes sont valides (même logique que le wizard traducteur).
  const goToStep = (n) => {
    if (n <= stepIdx + 1) {
      setError('');
      setAttempted(false);
      setStepIdx(n - 1);
      return;
    }
    const firstInvalid = steps.slice(0, n - 1).find((k) => getStepError(k));
    if (firstInvalid) {
      setAttempted(true);
      setStepIdx(steps.indexOf(firstInvalid));
      setError(getStepError(firstInvalid));
      return;
    }
    setError('');
    setAttempted(false);
    setStepIdx(n - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAttempted(true);
    setSuccess('');
    setError('');
    for (let i = 0; i < steps.length; i++) {
      const msg = getStepError(steps[i]);
      if (msg) {
        setStepIdx(i);
        setError(msg);
        return;
      }
    }

    const payload = editMode
      ? {
          civility: form.civility,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
          phone: form.phone.trim(),
          clientType: form.clientType,
          organizationName: form.organizationName.trim(),
          taxId: form.taxId.trim(),
        }
      : {
          civility: form.civility,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          password: form.password,
          confirmPassword: form.confirmPassword,
          clientType: form.clientType,
          organizationName: form.organizationName.trim(),
          taxId: form.taxId.trim(),
          acceptTerms: form.acceptTerms,
          acceptPrivacy: form.acceptPrivacy,
        };

    const result = await onSubmit(payload);
    if (!result?.ok) {
      setError(t.auth[result?.error] || result?.error || 'Erreur lors de l\'enregistrement.');
      return;
    }

    if (mode === 'edit') {
      if (onSuccess) onSuccess(result);
      return;
    }
    setSuccess(successMessage || t.auth.registerSuccess);
    setTimeout(() => setSuccess(''), 4000);
    setTimeout(() => {
      if (onSuccess) onSuccess(result);
    }, 1200);
  };

  const renderStepBlock = (key) => {
    switch (key) {
      case 'personal':
        return (
          <>
            <div style={rowStyle}>
              <div className="auth-field">
                <label htmlFor="reg-civility">Civilité <span className="dform-req">*</span></label>
                <div className="auth-input" style={{ paddingRight: 8 }}>
                  <User size={17} aria-hidden="true" />
                  <select
                    id="reg-civility"
                    required
                    value={form.civility}
                    onChange={(e) => update('civility', e.target.value)}
                    style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: 'var(--navy)', fontSize: '0.95rem' }}
                  >
                    <option value="">Sélectionner</option>
                    {civilities.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="auth-field">
                <label htmlFor="reg-firstName">Prénom <span className="dform-req">*</span></label>
                <div className="auth-input">
                  <User size={17} aria-hidden="true" />
                  <input
                    id="reg-firstName"
                    required
                    autoComplete="given-name"
                    placeholder="Votre prénom"
                    value={form.firstName}
                    onChange={(e) => update('firstName', e.target.value)}
                  />
                </div>
              </div>
              <div className="auth-field">
                <label htmlFor="reg-lastName">Nom <span className="dform-req">*</span></label>
                <div className="auth-input">
                  <User size={17} aria-hidden="true" />
                  <input
                    id="reg-lastName"
                    required
                    autoComplete="family-name"
                    placeholder="Votre nom"
                    value={form.lastName}
                    onChange={(e) => update('lastName', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="auth-field">
              <label htmlFor="reg-email">Email <span className="dform-req">*</span></label>
              <div className="auth-input" style={editMode ? { opacity: 0.7 } : undefined}>
                <Mail size={17} aria-hidden="true" />
                <input
                  id="reg-email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="email@exemple.com"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  disabled={editMode}
                />
              </div>
            </div>

            <div className="auth-field">
              <label htmlFor="reg-phone">Téléphone {editMode ? <span className="dform-req">*</span> : '(facultatif)'}</label>
              <div className="auth-input">
                <Phone size={17} aria-hidden="true" />
                <input
                  id="reg-phone"
                  type="tel"
                  required={editMode}
                  autoComplete="tel"
                  placeholder="+216 20 000 000"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                />
              </div>
            </div>
          </>
        );

      case 'account':
        return (
          <>
            <div style={rowStyle}>
              <div className="auth-field">
                <label htmlFor="reg-password">Mot de passe <span className="dform-req">*</span></label>
                <div className="auth-input">
                  <Lock size={17} aria-hidden="true" />
                  <input
                    id="reg-password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    minLength={8}
                    placeholder="••••••••"
                    value={form.password}
                    onChange={(e) => update('password', e.target.value)}
                  />
                  <button
                    type="button"
                    className="auth-eye"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? t.auth.hidePassword : t.auth.showPassword}
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                <div
                  style={{
                    height: 6,
                    borderRadius: 4,
                    background: '#e5e7eb',
                    marginTop: 8,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.min(100, getPasswordStrength(form.password) * 20)}%`,
                      background:
                        getPasswordStrength(form.password) <= 2
                          ? '#ef4444'
                          : getPasswordStrength(form.password) <= 3
                            ? '#f59e0b'
                            : getPasswordStrength(form.password) <= 4
                              ? '#3b82f6'
                              : '#10b981',
                      transition: 'all 0.2s ease',
                    }}
                  />
                </div>
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: '6px 0 0',
                    fontSize: '0.72rem',
                    color: 'var(--gray-500)',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '2px 10px',
                  }}
                >
                  {[
                    { label: '8 caractères', ok: form.password.length >= 8 },
                    { label: 'Majuscule', ok: /[A-Z]/.test(form.password) },
                    { label: 'Minuscule', ok: /[a-z]/.test(form.password) },
                    { label: 'Chiffre', ok: /[0-9]/.test(form.password) },
                    { label: 'Spécial', ok: /[^A-Za-z0-9]/.test(form.password) },
                  ].map((item) => (
                    <li key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 4, color: item.ok ? '#10b981' : 'var(--gray-500)' }}>
                      <span style={{ fontSize: '0.75rem' }}>{item.ok ? '✓' : '○'}</span>
                      {item.label}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="auth-field">
                <label htmlFor="reg-confirm">Confirmer le mot de passe <span className="dform-req">*</span></label>
                <div className="auth-input">
                  <Lock size={17} aria-hidden="true" />
                  <input
                    id="reg-confirm"
                    type={showConfirm ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    minLength={8}
                    placeholder="••••••••"
                    value={form.confirmPassword}
                    onChange={(e) => update('confirmPassword', e.target.value)}
                  />
                  <button
                    type="button"
                    className="auth-eye"
                    onClick={() => setShowConfirm((v) => !v)}
                    aria-label={showConfirm ? t.auth.hidePassword : t.auth.showPassword}
                  >
                    {showConfirm ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      case 'type':
        return (
          <>
            <div className="auth-field">
              <span style={{ display: 'block', marginBottom: '8px', fontSize: '0.84rem', fontWeight: '700', color: 'var(--navy)' }}>
                Vous êtes : <span className="dform-req">*</span>
              </span>
              <div style={radioGroupStyle}>
                {['particulier', 'entreprise', 'organisation'].map((type) => (
                  <label
                    key={type}
                    style={radioStyle(form.clientType === type)}
                  >
                    <input
                      type="radio"
                      name="clientType"
                      value={type}
                      checked={form.clientType === type}
                      onChange={() => update('clientType', type)}
                      style={{ width: 18, height: 18, accentColor: 'var(--teal)' }}
                    />
                    {type === 'particulier' && 'Particulier'}
                    {type === 'entreprise' && 'Entreprise'}
                    {type === 'organisation' && 'Organisation'}
                  </label>
                ))}
              </div>
            </div>

            {form.clientType !== 'particulier' && (
              <>
                <div className="auth-field" style={{ marginTop: '12px' }}>
                  <label htmlFor="reg-organization">Nom de l'entreprise / organisation <span className="dform-req">*</span></label>
                  <div className="auth-input">
                    <Building2 size={17} aria-hidden="true" />
                    <input
                      id="reg-organization"
                      placeholder="Raison sociale"
                      value={form.organizationName}
                      onChange={(e) => update('organizationName', e.target.value)}
                    />
                  </div>
                </div>

                <div className="auth-field">
                  <label htmlFor="reg-taxId">
                    Matricule fiscal ou identifiant professionnel (optionnel)
                  </label>
                  <div className="auth-input">
                    <Briefcase size={17} aria-hidden="true" />
                    <input
                      id="reg-taxId"
                      placeholder="Optionnel"
                      value={form.taxId}
                      onChange={(e) => update('taxId', e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
          </>
        );

      case 'terms':
        return (
          <div className="auth-field" style={{ display: 'grid', gap: '10px' }}>
            <label style={checkboxStyle}>
              <input
                type="checkbox"
                checked={form.acceptTerms}
                onChange={(e) => update('acceptTerms', e.target.checked)}
                style={{ width: 18, height: 18, accentColor: 'var(--teal)', marginTop: '2px' }}
              />
              J'accepte les conditions générales d'utilisation. <span className="dform-req">*</span>
            </label>
            <label style={checkboxStyle}>
              <input
                type="checkbox"
                checked={form.acceptPrivacy}
                onChange={(e) => update('acceptPrivacy', e.target.checked)}
                style={{ width: 18, height: 18, accentColor: 'var(--teal)', marginTop: '2px' }}
              />
              J'accepte la politique de confidentialité. <span className="dform-req">*</span>
            </label>
            <label style={checkboxStyle}>
              <input
                type="checkbox"
                checked={form.confirmInfo}
                onChange={(e) => update('confirmInfo', e.target.checked)}
                style={{ width: 18, height: 18, accentColor: 'var(--teal)', marginTop: '2px' }}
              />
              Je confirme l'exactitude des informations fournies. <span className="dform-req">*</span>
            </label>
          </div>
        );

      default:
        return null;
    }
  };

  const stepContent = (() => {
    switch (stepKey) {
      case 'register':
        return (
          <>
            {renderStepBlock('personal')}
            <div style={sectionDividerStyle}>Type de client</div>
            {renderStepBlock('type')}
            <div style={sectionDividerStyle}>Sécurité du compte</div>
            {renderStepBlock('account')}
            <div style={sectionDividerStyle}>Conditions</div>
            {renderStepBlock('terms')}
          </>
        );
      case 'profile':
        return renderStepBlock('type');
      case 'edit':
        return (
          <>
            {renderStepBlock('personal')}
            <div style={sectionDividerStyle}>Type de client</div>
            {renderStepBlock('type')}
          </>
        );
      default:
        return null;
    }
  })();

  return (
    <>
      {success && (
        <div className="auth-toast" role="status">
          <CheckCircle2 size={18} />
          <span>{success}</span>
        </div>
      )}
    <form className="auth-card auth-card--wide" onSubmit={(e) => e.preventDefault()} noValidate style={{ width: '100%' }}>
      {(title || subtitle) && (
        <div className="auth-card-head" style={{ marginBottom: 16 }}>
          {title && <h2 style={{ fontSize: '1.35rem' }}>{title}</h2>}
          {subtitle && <p style={{ fontSize: '0.85rem' }}>{subtitle}</p>}
        </div>
      )}

      {steps.length > 1 && (
        <DashFormStepper steps={steps.map((k) => stepLabel(k))} current={stepIdx + 1} onSelect={goToStep} />
      )}

      <div>
        {stepContent}
      </div>

      {error && (
        <div className="auth-error" role="alert" style={{ marginTop: 16, padding: '10px 12px' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', gap: '12px' }}>
        {steps.length > 1 ? (
          <button
            type="button"
            className="btn btn-outline"
            onClick={goPrev}
            disabled={stepIdx === 0}
            style={{
              background: stepIdx === 0 ? 'var(--gray-100)' : '#fff',
              borderColor: stepIdx === 0 ? 'var(--gray-300)' : 'var(--teal)',
              color: stepIdx === 0 ? 'var(--gray-500)' : 'var(--teal)',
            }}
          >
            <ChevronLeft size={18} />
            Précédent
          </button>
        ) : (
          <span />
        )}

        {isLastStep ? (
          <button type="button" className="btn btn-primary" onClick={handleSubmit}>
            {editMode ? <Check size={18} /> : <UserPlus size={18} />}
            {submitLabel || (editMode ? t.dashboard?.save || 'Enregistrer' : t.auth.registerButton)}
          </button>
        ) : (
          <button type="button" className="btn btn-primary" onClick={goNext}>
            Suivant
            <ChevronRight size={18} />
          </button>
        )}
      </div>

      {footer}
    </form>
    </>
  );
}
